import type { JSX } from 'react';
import {
  Alert,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { HomePage } from './HomePage';

type WorkspaceRedirectState = {
  workspaceUnavailable?: 'platform';
};

function hasPlatformUnavailableState(state: unknown): state is WorkspaceRedirectState {
  return (
    typeof state === 'object'
    && state !== null
    && (state as WorkspaceRedirectState).workspaceUnavailable === 'platform'
  );
}

export function WorkspaceHomePage(): JSX.Element {
  const auth = useAuth();
  const location = useLocation();
  const redirectState = hasPlatformUnavailableState(location.state) ? location.state : undefined;

  if (auth.isLoading) {
    return (
      <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading workspace...</Typography>
      </Stack>
    );
  }

  if (!auth.bootstrap) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (auth.bootstrap.mode === 'identity') {
    if (auth.bootstrap.memberships.length === 0) {
      return auth.canAccessPlatform
        ? <Navigate to="/platform" replace />
        : <Navigate to="/onboarding" replace state={redirectState} />;
    }

    if (auth.bootstrap.nextAction === 'select-company') {
      return <Navigate to="/select-company" replace state={redirectState} />;
    }

    if (auth.bootstrap.nextAction === 'auto-select-company') {
      return (
        <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
          <CircularProgress />
          <Typography color="text.secondary">Opening company workspace...</Typography>
        </Stack>
      );
    }
  }

  return (
    <Stack spacing={2.5}>
      {redirectState?.workspaceUnavailable === 'platform' && (
        <Alert severity="info">
          Platform workspace is not available for this account.
        </Alert>
      )}
      <HomePage />
    </Stack>
  );
}
